import type { JSX } from 'react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useEncountersByHealthieId } from '@/hooks/useEncounters';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import {
  EncounterSummary,
  ENCOUNTER_TYPES,
  ENCOUNTER_TYPES_API,
  encounterTypeMapping
} from '@/types/encounter';
import { Alert as CalloutAlert } from '@/ui/Alert';
import Alert from '@/ui/components/Alert';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import { CopyContent } from '@/ui/CopyContent';

import EncounterItem from './EncounterItem';

const EncounterList = (): JSX.Element | null => {
  const { encounterType, encounterHealthieId } = useParams();
  const isQueryEnabled = !!encounterHealthieId && !!encounterType;
  const { navigateToEncounterDetails, navigateToAppointmentById } = useNavigateToPage();

  const {
    data: encounterListResponse,
    isError,
    error,
    isLoading: isEncounterListLoading
  } = useEncountersByHealthieId({
    healthieId: encounterHealthieId,
    encounterType: encounterType,
    enabled: isQueryEnabled
  });

  useEffect(() => {
    const maybeBillableEncounters = encounterListResponse?.filter(
      (encounter: EncounterSummary) => encounter.maybe_billable
    );

    // Multiple billable appointment attendances sharing an appointment → go to appointment detail
    const billableAttendances = maybeBillableEncounters?.filter(
      (e) => e.encounter_type === ENCOUNTER_TYPES_API.APPOINTMENT_ATTENDANCE
    );
    const appointmentId = billableAttendances?.[0]?.appointment_attendance?.appointment.id;
    if ((billableAttendances?.length ?? 0) > 1 && appointmentId) {
      navigateToAppointmentById(appointmentId, { replace: true });
      return;
    }

    // A 1:1 appointment can also carry billable forms, drug screens, or add-ons;
    // the attendance is the entry point for all of them
    if (billableAttendances?.length === 1) {
      navigateToEncounterDetails(billableAttendances[0], { replace: true });
      return;
    }

    if (encounterListResponse?.length === 1) {
      navigateToEncounterDetails(encounterListResponse[0], { replace: true });
    }

    if (maybeBillableEncounters?.length === 1) {
      navigateToEncounterDetails(maybeBillableEncounters[0], { replace: true });
    }
  }, [encounterListResponse, navigateToEncounterDetails, navigateToAppointmentById]);

  if (isEncounterListLoading && isQueryEnabled && !isError) {
    return (
      <div className="flex items-center p-3">
        <Spinner label="Loading Encounter list ..." />
      </div>
    );
  }

  if (isQueryEnabled && isError && error.message)
    return <Alert level="error">{error.message}</Alert>;

  if (!encounterListResponse?.length)
    return (
      <div className="text-center p-8">
        <Heading text="No encounters found" value={3} />
      </div>
    );

  const hasAttendance = encounterListResponse.some(
    (encounter) => encounter.encounter_type === ENCOUNTER_TYPES_API.APPOINTMENT_ATTENDANCE
  );
  const encounterLabel = (
    Object.values(encounterTypeMapping).find(({ path }) => path === encounterType)?.label ??
    'encounter'
  ).toLowerCase();
  const missingAttendanceMessage =
    encounterType === ENCOUNTER_TYPES.APPOINTMENT
      ? 'This appointment has no attendance to bill against.'
      : `This ${encounterLabel} is not attached to any appointment.`;

  return (
    <div>
      {!hasAttendance && (
        <CalloutAlert variant="error" className="m-3">
          <p className="font-semibold">{missingAttendanceMessage}</p>
          <CopyContent
            content={`${missingAttendanceMessage}\n${window.location.href}`}
            className="mt-1"
            buttonClassName="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
            Copy this error
          </CopyContent>
        </CalloutAlert>
      )}
      <ul>
        {encounterListResponse.map((encounter: EncounterSummary) => (
          <li key={encounter?.encounter_id}>
            <EncounterItem encounter={encounter} />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default EncounterList;
